import type { DataTableRow } from "@client-shared";
import type { LabelPrintBatch } from "@shared/types";
import type { ColumnDef, HeaderContext } from "@tanstack/react-table";
import { readableBatchId } from "@client-shared/lib/util";
import { useTranslation } from "react-i18next";
import {
  BatchIdCellMarkup,
  createBatchActionsCellMarkup,
  BatchNotesCellMarkup,
  BatchStatusCellMarkup,
  CreatedAtCellMarkup,
  WeightCellMarkup,
} from "./cells";
import { setBatchesTableHeaderTestId } from "./batches-table.utils";

const BatchHeaderCell = ({ column }: HeaderContext<DataTableRow<LabelPrintBatch>, unknown>) => {
  const { t } = useTranslation();
  const headerLabels: Record<string, string> = {
    batchId: t("views.batchesTable.columns.batchId"),
    batchStatus: t("views.batchesTable.columns.status"),
    createdAt: t("views.batchesTable.columns.created"),
    batchNotes: t("views.batchesTable.columns.notes", {
      defaultValue: "Notes",
    }),
    totalOrders: t("views.batchesTable.columns.orders"),
    totalUnits: t("views.batchesTable.columns.units", {
      defaultValue: "Units",
    }),
    totalWeightInGrams: t("views.batchesTable.columns.weight"),
    actions: t("views.batchesTable.columns.actions"),
  };

  return <span data-testid={setBatchesTableHeaderTestId(column.id)}>{headerLabels[column.id] ?? column.id}</span>;
};

interface CreateBatchesColumnsOptions {
  onSelectBatch?: (batchId: string) => void;
}

export const createBatchesColumns = ({ onSelectBatch }: CreateBatchesColumnsOptions = {}): ColumnDef<DataTableRow<LabelPrintBatch>>[] => [
  {
    id: "batchId",
    accessorFn: (row) => readableBatchId(row.batchId),
    header: BatchHeaderCell,
    cell: BatchIdCellMarkup,
  },
  {
    id: "batchStatus",
    accessorKey: "batchStatus",
    header: BatchHeaderCell,
    cell: BatchStatusCellMarkup,
  },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: BatchHeaderCell,
    cell: CreatedAtCellMarkup,
  },
  {
    id: "batchNotes",
    accessorKey: "batchNotes",
    header: BatchHeaderCell,
    cell: BatchNotesCellMarkup,
  },
  {
    id: "totalOrders",
    accessorKey: "totalOrders",
    header: BatchHeaderCell,
  },
  {
    id: "totalUnits",
    accessorKey: "totalUnits",
    header: BatchHeaderCell,
  },
  {
    id: "totalWeightInGrams",
    accessorKey: "totalWeightInGrams",
    header: BatchHeaderCell,
    cell: WeightCellMarkup,
  },
  {
    id: "actions",
    enableSorting: false,
    enableColumnFilter: false,
    header: BatchHeaderCell,
    cell: createBatchActionsCellMarkup({
      onSelectBatch,
    }),
  },
];
